import React, { useEffect, useRef, useState } from 'react';

const FOCUSABLE_ELEMENT_SELECTOR = 'a[href],[tabindex="0"]';

enum KeyboardKey {
  ENTER = 'Enter',
  ARROW_DOWN = 'ArrowDown',
  ARROW_UP = 'ArrowUp',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  TAB = 'Tab',
}

const useNavKeyboardControl = <T extends HTMLElement>(): React.RefObject<T> => {
  const linksSectionsRef = useRef<T | null>(null);
  const focusIndex = useRef<number | null>(0);
  const [sectionIndex, setSectionIndex] = useState<number>(0);

  const getSelectedSubmenu = (
    nextSectionIndex?: number,
    getLastSubmenu?: boolean
  ): HTMLElement | null => {
    const navList = linksSectionsRef.current;
    if (!navList) {
      console.error('Footer links sections not found.');
      return null;
    }

    const sections = navList.querySelectorAll('[aria-labelledby]');

    if (!sections || sections.length === 0) {
      console.error(`The footer section does not contain any list.`);
      return null;
    }

    if (getLastSubmenu) {
      setSectionIndex(sections.length - 1);
    }

    return sections[
      getLastSubmenu ? sections.length - 1 : nextSectionIndex ?? sectionIndex
    ] as HTMLElement;
  };

  const findNavItem = (
    navItems: Element[],
    callback?: (element: HTMLElement) => void
  ): HTMLElement | undefined => {
    if (focusIndex.current === null) {
      return;
    }
    const element = navItems[focusIndex.current].firstElementChild as HTMLElement;
    if (!element) {
      console.error(
        'No focusable element found in the nav item. Please make sure the element is or has an anchor.'
      );
    }

    if (
      (element.tagName === 'A' && element?.getAttribute('href')) ||
      element?.getAttribute('tabindex')
    ) {
      callback?.(element);
      return element;
    }

    const interactableElement = element.querySelector(
      FOCUSABLE_ELEMENT_SELECTOR
    ) as HTMLElement;
    if (interactableElement) {
      callback?.(interactableElement);
      return interactableElement;
    }

    console.error(
      'No focusable element found in the nav item. Please make sure the element is or has an anchor.'
    );
  };

  const focusNextMenu = (): void => {
    const nextList = getSelectedSubmenu(sectionIndex + 1);
    if (nextList) {
      setSectionIndex((prev) => prev + 1);
      (nextList.querySelector(FOCUSABLE_ELEMENT_SELECTOR) as HTMLElement)?.focus();
      focusIndex.current = 0;
      return;
    }
    const firstList = getSelectedSubmenu(0);
    if (!firstList) {
      return;
    }
    setSectionIndex(0);
    (firstList.querySelector(FOCUSABLE_ELEMENT_SELECTOR) as HTMLElement)?.focus();
    focusIndex.current = 0;
    return;
  };

  // Focuses the next item in the section list.
  // If we're at the end of the list, we're looking for the next section list
  // and focusing on the first element in that list.
  const findNextItem = (
    currentMenu: HTMLElement,
    callback?: (element: HTMLElement) => void
  ): void => {
    const children = Array.from(currentMenu.children);
    if (children.length === 0) {
      focusIndex.current = null;
      return;
    }
    if (focusIndex.current === children.length - 1) {
      focusNextMenu();
      return;
    }
    const newIndex =
      focusIndex.current === null
        ? 0
        : focusIndex.current === children.length - 1
        ? 0
        : focusIndex.current + 1;
    focusIndex.current = newIndex;
    findNavItem(children, (element) => callback?.(element));
  };

  const focusNextItem = (currentMenu: HTMLElement): void => {
    findNextItem(currentMenu, (element) => element.focus());
  };

  const focusPreviousMenu = (focusLastItem?: boolean): void => {
    const nextList = getSelectedSubmenu(sectionIndex - 1);
    if (nextList) {
      setSectionIndex((prev) => prev - 1);
      if (!focusLastItem) {
        (nextList.querySelector(FOCUSABLE_ELEMENT_SELECTOR) as HTMLElement)?.focus();
        focusIndex.current = 0;
        return;
      }

      const anchorElements = Array.from(
        nextList.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR)
      ) as HTMLElement[];
      anchorElements[anchorElements.length - 1]?.focus();
      focusIndex.current = anchorElements.length - 1;
      return;
    }
    const lastList = getSelectedSubmenu(undefined, true);
    if (!lastList) {
      return;
    }

    if (!focusLastItem) {
      (lastList.querySelector(FOCUSABLE_ELEMENT_SELECTOR) as HTMLElement)?.focus();
      focusIndex.current = 0;
      return;
    }

    const anchorElements = Array.from(
      lastList.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR)
    ) as HTMLElement[];
    anchorElements[anchorElements.length - 1]?.focus();
    focusIndex.current = anchorElements.length - 1;
  };

  // Focuses the previous item in the section list.
  // If we're at the beginning of the list, we're looking for the previous section list
  // and focusing on the last element in that list.
  const findPreviousItem = (
    currentMenu: HTMLElement,
    callback?: (element: HTMLElement) => void
  ): void => {
    const children = Array.from(currentMenu.children);
    if (children.length === 0) {
      focusIndex.current = null;
      return;
    }
    if (focusIndex.current === 0) {
      focusPreviousMenu(true);
      return;
    }
    const newIndex =
      focusIndex.current === null
        ? 0
        : focusIndex.current === 0
        ? children.length - 1
        : focusIndex.current - 1;
    focusIndex.current = newIndex;
    findNavItem(children, (element) => callback?.(element));
  };

  const focusPreviousItem = (currentMenu: HTMLElement): void => {
    findPreviousItem(currentMenu, (element) => element.focus());
  };

  // Keyboard controls:
  //   - Arrow Down - Select next item
  //   - Arrow Right - Select next menu
  //   - Arrow Up - Select previous item
  //   - Arrow Left - Select previous menu

  // When dropdown selected, the controls should be limited to these options
  const handleKeyboardControls = (e: KeyboardEvent, section: HTMLElement): void => {
    const children = Array.from(section.children);
    if (children.length === 0) return;

    const { ARROW_UP, ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT, ENTER, TAB } = KeyboardKey;
    if (e.key !== ENTER && e.key !== TAB) {
      e.preventDefault();
    }

    switch (e.key) {
      case ARROW_UP:
        focusPreviousItem(section);
        break;
      case ARROW_LEFT:
        focusPreviousMenu();
        break;
      case ARROW_DOWN:
        focusNextItem(section);
        break;
      case ARROW_RIGHT:
        focusNextMenu();
        break;
    }
  };

  useEffect(() => {
    if (!linksSectionsRef.current) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      const navList = linksSectionsRef.current;
      if (!navList) return;

      const currentSection = getSelectedSubmenu(sectionIndex);
      if (!currentSection) return;

      handleKeyboardControls(e, currentSection);
    };

    const navList = linksSectionsRef.current;
    if (!navList) {
      return;
    }

    navList.addEventListener('keydown', onKeyDown);

    return () => navList.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linksSectionsRef, sectionIndex]);

  return linksSectionsRef;
};

export default useNavKeyboardControl;
